import { Injectable, inject, OnDestroy } from '@angular/core';
import { Observable, Subscription, interval, timer } from 'rxjs';
import { switchMap, catchError, tap } from 'rxjs/operators';
import { LoggerService } from './logger.service';
import { StorageService } from './storage.service';

export interface PollingTask<T = unknown> {
    /** Unique identifier for this polling task */
    id: string;
    /** Name/description of the task */
    name: string;
    /** Interval in milliseconds between polls */
    intervalMs: number;
    /** The API call function that returns an Observable */
    apiCall: () => Observable<T>;
    /** Optional: Transform data before storing */
    parseData?: (data: T) => unknown;
    /** Optional: Custom storage key. Defaults to task id */
    storageKey?: string;
    /** Optional: Callback when polling succeeds */
    onSuccess?: (data: unknown) => void;
    /** Optional: Callback when polling fails */
    onError?: (error: unknown) => void;
    /** Optional: Whether to store data in localStorage (default: true) */
    storeInLocalStorage?: boolean;
    /** Optional: Delay before starting first poll (default: 0) */
    initialDelayMs?: number;
    /** Optional: Maximum number of retries on error (default: 0) */
    maxRetries?: number;
    /** Optional: Whether task is enabled (default: true) */
    enabled?: boolean;
}

interface ActiveTask {
    task: PollingTask;
    subscription: Subscription;
    isRunning: boolean;
    lastPollTime?: Date;
    lastSuccessTime?: Date;
    errorCount: number;
    successCount: number;
}

@Injectable({
    providedIn: 'root'
})
export class PollingService implements OnDestroy {
    private logger = inject(LoggerService);
    private storage = inject(StorageService);
    private activeTasks = new Map<string, ActiveTask>();
    private isServiceActive = false;

    constructor() {
        this.logger.log('PollingService initialized');
    }

    /**
     * Start the polling service
     */
    start(): void {
        if (!this.isServiceActive) {
            this.isServiceActive = true;
            this.logger.log('PollingService started');
            this.startAllTasks();
        }
    }

    /**
     * Stop the polling service and all tasks
     */
    stop(): void {
        if (this.isServiceActive) {
            this.isServiceActive = false;
            this.stopAllTasks();
            this.logger.log('PollingService stopped');
        }
    }

    /**
     * Add a new polling task
     */
    addTask<T>(task: PollingTask<T>): void {
        if (this.activeTasks.has(task.id)) {
            this.logger.warn(`Task with id '${task.id}' already exists. Removing old task.`);
            this.removeTask(task.id);
        }

        // Set defaults
        const taskWithDefaults: PollingTask<T> = {
            ...task,
            storeInLocalStorage: task.storeInLocalStorage !== false,
            initialDelayMs: task.initialDelayMs || 0,
            maxRetries: task.maxRetries || 0,
            enabled: task.enabled !== false,
            storageKey: task.storageKey || `polling_${task.id}`
        };

        const activeTask: ActiveTask = {
            task: taskWithDefaults,
            subscription: new Subscription(),
            isRunning: false,
            errorCount: 0,
            successCount: 0
        };

        this.activeTasks.set(task.id, activeTask);
        this.logger.log(`Added polling task: ${task.name} (${task.id})`);

        // Start the task if service is active and task is enabled
        if (this.isServiceActive && taskWithDefaults.enabled) {
            this.startTask(task.id);
        }
    }

    /**
     * Remove a polling task
     */
    removeTask(taskId: string): void {
        const activeTask = this.activeTasks.get(taskId);
        if (activeTask) {
            this.stopTask(taskId);
            this.activeTasks.delete(taskId);
            this.logger.log(`Removed polling task: ${taskId}`);
        }
    }

    /**
     * Start a specific task
     */
    startTask(taskId: string): void {
        const activeTask = this.activeTasks.get(taskId);
        if (!activeTask) {
            this.logger.warn(`Task '${taskId}' not found`);
            return;
        }

        if (activeTask.isRunning) {
            this.logger.warn(`Task '${taskId}' is already running`);
            return;
        }

        if (!activeTask.task.enabled) {
            this.logger.warn(`Task '${taskId}' is disabled`);
            return;
        }

        const task = activeTask.task;
        this.logger.log(`Starting polling task: ${task.name} (interval: ${task.intervalMs}ms)`);

        // Create observable with initial delay
        const polling$ = timer(task.initialDelayMs || 0, task.intervalMs).pipe(
            tap(() => {
                activeTask.lastPollTime = new Date();
            }),
            switchMap(() => task.apiCall()),
            tap(data => this.handleSuccess(taskId, data)),
            catchError(error => {
                this.handleError(taskId, error);
                return []; // Continue polling even after error
            })
        );

        activeTask.subscription = polling$.subscribe();
        activeTask.isRunning = true;
    }

    /**
     * Stop a specific task
     */
    stopTask(taskId: string): void {
        const activeTask = this.activeTasks.get(taskId);
        if (activeTask && activeTask.isRunning) {
            activeTask.subscription.unsubscribe();
            activeTask.isRunning = false;
            this.logger.log(`Stopped polling task: ${taskId}`);
        }
    }

    /**
     * Pause a task (can be resumed later)
     */
    pauseTask(taskId: string): void {
        this.stopTask(taskId);
    }

    /**
     * Resume a paused task
     */
    resumeTask(taskId: string): void {
        this.startTask(taskId);
    }

    /**
     * Enable a task
     */
    enableTask(taskId: string): void {
        const activeTask = this.activeTasks.get(taskId);
        if (activeTask) {
            activeTask.task.enabled = true;
            if (this.isServiceActive) {
                this.startTask(taskId);
            }
        }
    }

    /**
     * Disable a task
     */
    disableTask(taskId: string): void {
        const activeTask = this.activeTasks.get(taskId);
        if (activeTask) {
            activeTask.task.enabled = false;
            this.stopTask(taskId);
        }
    }

    /**
     * Get task status
     */
    getTaskStatus(taskId: string): ActiveTask | undefined {
        return this.activeTasks.get(taskId);
    }

    /**
     * Get all tasks
     */
    getAllTasks(): Map<string, ActiveTask> {
        return new Map(this.activeTasks);
    }

    /**
     * Get data from storage for a task
     */
    async getStoredData<T>(taskId: string): Promise<T | null> {
        const activeTask = this.activeTasks.get(taskId);
        const storageKey = activeTask?.task.storageKey || `polling_${taskId}`;

        try {
            return await this.storage.get<T>(storageKey);
        } catch (error) {
            this.logger.error(`Failed to retrieve stored data for task '${taskId}':`, error);
            return null;
        }
    }

    /**
     * Manually trigger a poll for a specific task
     */
    triggerPoll(taskId: string): void {
        const activeTask = this.activeTasks.get(taskId);
        if (!activeTask) {
            this.logger.warn(`Task '${taskId}' not found`);
            return;
        }

        this.logger.log(`Manually triggering poll for task: ${taskId}`);
        activeTask.task.apiCall().subscribe({
            next: data => this.handleSuccess(taskId, data),
            error: error => this.handleError(taskId, error)
        });
    }

    /**
     * Clear stored data for a task
     */
    async clearStoredData(taskId: string): Promise<void> {
        const activeTask = this.activeTasks.get(taskId);
        const storageKey = activeTask?.task.storageKey || `polling_${taskId}`;

        try {
            await this.storage.delete(storageKey);
            this.logger.log(`Cleared stored data for task: ${taskId}`);
        } catch (error) {
            this.logger.error(`Failed to clear stored data for task '${taskId}':`, error);
        }
    }

    /**
     * Handle successful API call
     */
    private handleSuccess(taskId: string, data: unknown): void {
        const activeTask = this.activeTasks.get(taskId);
        if (!activeTask) return;

        const task = activeTask.task;
        activeTask.lastSuccessTime = new Date();
        activeTask.successCount++;
        activeTask.errorCount = 0; // Reset error count on success

        this.logger.log(`Poll successful for task '${task.name}': ${activeTask.successCount} total successes`);

        // Parse data if parser function is provided
        let parsedData = data;
        if (task.parseData) {
            try {
                parsedData = task.parseData(data);
                this.logger.debug(`Data parsed for task '${task.name}'`);
            } catch (error) {
                this.logger.error(`Error parsing data for task '${task.name}':`, error);
                // Continue with unparsed data
            }
        }

        // Store in localStorage if enabled
        if (task.storeInLocalStorage) {
            this.storeData(task.storageKey!, parsedData);
        }

        // Call success callback if provided
        if (task.onSuccess) {
            try {
                task.onSuccess(parsedData);
            } catch (error) {
                this.logger.error(`Error in onSuccess callback for task '${task.name}':`, error);
            }
        }
    }

    /**
     * Handle API call error
     */
    private handleError(taskId: string, error: unknown): void {
        const activeTask = this.activeTasks.get(taskId);
        if (!activeTask) return;

        const task = activeTask.task;
        activeTask.errorCount++;

        this.logger.error(`Poll failed for task '${task.name}' (error count: ${activeTask.errorCount}):`, error);

        // Stop task if max retries exceeded
        if (task.maxRetries && activeTask.errorCount >= task.maxRetries) {
            this.logger.error(`Max retries exceeded for task '${task.name}'. Stopping task.`);
            this.stopTask(taskId);
        }

        // Call error callback if provided
        if (task.onError) {
            try {
                task.onError(error);
            } catch (callbackError) {
                this.logger.error(`Error in onError callback for task '${task.name}':`, callbackError);
            }
        }
    }

    /**
     * Store data using StorageService
     */
    private async storeData(storageKey: string, data: unknown): Promise<void> {
        try {
            await this.storage.set(storageKey, data);
            this.logger.debug(`Stored data with key: ${storageKey}`);
        } catch (error) {
            this.logger.error(`Failed to store data with key '${storageKey}':`, error);
        }
    }

    /**
     * Start all enabled tasks
     */
    private startAllTasks(): void {
        this.activeTasks.forEach((activeTask, taskId) => {
            if (activeTask.task.enabled && !activeTask.isRunning) {
                this.startTask(taskId);
            }
        });
    }

    /**
     * Stop all running tasks
     */
    private stopAllTasks(): void {
        this.activeTasks.forEach((_, taskId) => {
            this.stopTask(taskId);
        });
    }

    /**
     * Cleanup on service destroy
     */
    ngOnDestroy(): void {
        this.stop();
        this.activeTasks.clear();
        this.logger.log('PollingService destroyed');
    }
}
